import React, { useEffect, useState, useMemo, useRef } from "react";
import { Typography, Box, CircularProgress, Alert, Button, TextField, MenuItem, Select, InputLabel, FormControl, Checkbox, Autocomplete } from "@mui/material";
import { useNavigate } from "react-router-dom";
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { API_ENDPOINTS, API_BASE_URL } from "../config/api.config";

import { useCurrentUser } from '../hooks/useCurrentUser';

import { Tooltip as MuiTooltip } from '@mui/material';
import { Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper } from '@mui/material';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { getAllAbsencesForMonth } from '../utils/absenceService';

export default function MonthlyPeopleManagerView() {
  const { userId } = useCurrentUser();
  const navigate = useNavigate();

  // Debug log per verificare i ruoli
  console.log('MonthlyPeopleManagerView - userId:', userId);

  const [managedUsers, setManagedUsers] = useState([]); // Utenti gestiti dal people manager
  const [managedUsersInitialized, setManagedUsersInitialized] = useState(false);
  const [activities, setActivities] = useState([]);

  // Refs per evitare chiamate duplicate
  const isFetchingUsers = useRef(false);
  const isFetchingActivities = useRef(false);
  const isFetchingPivotData = useRef(false);
  const lastPivotFetchKey = useRef('');

  // Filtri per utente gestito e intervallo date (ora a selezione multipla)
  const [filterUsers, setFilterUsers] = useState([]);
  // Filtro per Activity (multi) - memorizza gli ID
  const [filterActivityIds, setFilterActivityIds] = useState([]);
  // Sostituisco i filtri data con mese/anno
  const now = new Date();
  const [filterMonth, setFilterMonth] = useState(now.getMonth() + 1); // 1-12
  const [filterYear, setFilterYear] = useState(now.getFullYear());
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const yearOptions = [];
  for (let y = 2020; y <= now.getFullYear() + 1; y++) yearOptions.push(y);

  // Carica tutti gli utenti gestiti all'avvio per il filtro
  useEffect(() => {
    // Evita fetch fino a quando userId non è disponibile
    if (!userId) {
      setManagedUsers([]);
      setManagedUsersLoaded(false);
      return;
    }

    // Evita chiamate duplicate
    if (isFetchingUsers.current) {
      return;
    }

    // Reset flag per auto-set filter users quando userId cambia
    hasAutoSetFilterUsers.current = false;

    // Svuota lo stato
    setManagedUsersInitialized(false);
    setManagedUsers([]);
    setFilterUsers([]);
    setManagedUsersLoaded(false);
    lastPivotFetchKey.current = ''; // Reset chiave pivot quando cambia userId

    isFetchingUsers.current = true;
    console.log('Fetching managed users for people manager userId:', userId);

    // Carica gli utenti che hanno questo user come people manager
    fetch(`${API_ENDPOINTS.USERS}?id_people_manager=${userId}`)
      .then(res => res.json())
      .then(data => {
        const arr = data.users || [];
        // Per ogni utente, verifica se è People Manager di altri
        Promise.all(arr.map(async (user) => {
          try {
            const checkRes = await fetch(`${API_ENDPOINTS.USERS}?id_people_manager=${user.id}`);
            const checkData = await checkRes.json();
            const managedCount = (checkData.users || []).length;
            return {
              id: user.id,
              name: user.username || (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email),
              username: user.username || (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email),
              email: user.email,
              org_role_id: user.org_role_id,
              isPeopleManager: managedCount > 0
            };
          } catch (err) {
            console.error(`Error checking if user ${user.id} is people manager:`, err);
            return {
              id: user.id,
              name: user.username || (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email),
              username: user.username || (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email),
              email: user.email,
              org_role_id: user.org_role_id,
              isPeopleManager: false
            };
          }
        })).then(formattedUsers => {
          console.log('Managed users loaded:', formattedUsers.length);
          setManagedUsers(formattedUsers);
          setManagedUsersLoaded(true);
          setManagedUsersInitialized(true);
          isFetchingUsers.current = false;
        });
      })
      .catch((err) => {
        console.error('Error fetching managed users:', err);
        setManagedUsers([]);
        setManagedUsersLoaded(true);
        setManagedUsersInitialized(true);
        isFetchingUsers.current = false;
      });
  }, [userId]);

  // Imposta automaticamente "Select All" per tutti gli utenti gestiti
  // Usa useRef per evitare loop infiniti quando filterUsers cambia
  const hasAutoSetFilterUsers = useRef(false);
  useEffect(() => {
    if (managedUsers.length > 0 && !hasAutoSetFilterUsers.current) {
      // Imposta tutti gli utenti gestiti come selezionati (Select All)
      const allUserNames = managedUsers.map(u => u.name || u.username);
      setFilterUsers(allUserNames);
      hasAutoSetFilterUsers.current = true;
      console.log('Auto-setting Select All for managed users:', allUserNames);
    }
  }, [managedUsers]);

  // Carica Activities per il filtro
  useEffect(() => {
    // Evita chiamate duplicate
    if (isFetchingActivities.current || activities.length > 0) {
      return;
    }

    isFetchingActivities.current = true;
    fetch(API_ENDPOINTS.ACTIVITIES)
      .then(res => res.json())
      .then(data => {
        const arr = data.activities || [];
        setActivities(Array.isArray(arr) ? arr : []);
        isFetchingActivities.current = false;
      })
      .catch(() => {
        setActivities([]);
        isFetchingActivities.current = false;
      });
  }, [activities.length]);

  // Rimosso fetchWorklogs standalone - questa vista usa solo la tabella pivot

  // Filtro rimosso - ora usiamo solo la tabella pivot filtrata

  // Ricava i campi editabili (escludi id, created_at, updated_at)
  // const getEditableFields = () => {
  //   if (!columns.length) return [];
  //   return columns.filter(col => !['id', 'created_at', 'updated_at'].includes(col.field));
  // };

  // Gestione apertura dialog - non utilizzata in questa vista (solo visualizzazione)

  // Rimuovo la funzione handleExportExcel

  // Azioni e colonne - non utilizzate in questa vista (solo visualizzazione)

  // Stato per la tabella pivot
  const [pivotData, setPivotData] = useState([]);
  const [pivotLoading, setPivotLoading] = useState(false);
  const [pivotError, setPivotError] = useState(null);
  // const [pivotMode, setPivotMode] = useState('user'); // 'user' o 'project'
  // const [pivotDetails, setPivotDetails] = useState({});
  // Stato per l'utente selezionato per i dettagli (per utenti non People Manager)
  const [selectedUserForDetails, setSelectedUserForDetails] = useState(null);
  const [detailsPivotData, setDetailsPivotData] = useState({});
  const [teamPivotData, setTeamPivotData] = useState({});
  const [teamDetailsLoading, setTeamDetailsLoading] = useState(false);

  // Evita flash di utenti non autorizzati prima che siano caricati
  const [managedUsersLoaded, setManagedUsersLoaded] = useState(false);
  const [absencesByUserId, setAbsencesByUserId] = useState({});

  // Calcola giorni del mese selezionato
  const daysInMonth = useMemo(() => {
    return new Date(filterYear, filterMonth, 0).getDate();
  }, [filterYear, filterMonth]);

  // Dimensioni fisse per tabella responsive con scroll orizzontale
  const tableDimensions = useMemo(() => {
    const firstColWidth = 140; // px
    const dayColWidth = 45; // px per ciascun giorno
    const totalColWidth = 60; // px
    const minWidth = firstColWidth + daysInMonth * dayColWidth + totalColWidth;
    return { firstColWidth, dayColWidth, totalColWidth, minWidth };
  }, [daysInMonth]);

  // Funzione per formattare i nomi utente (abbrevia solo la prima parte)
  const formatUserName = (fullName) => {
    if (!fullName || typeof fullName !== 'string') return fullName;

    const parts = fullName.trim().split(' ');
    if (parts.length <= 1) return fullName;

    // Abbrevia solo la prima parte: prima lettera + punto
    const firstPart = parts[0];
    const abbreviatedFirst = firstPart.charAt(0) + '.';

    // Mantieni il resto del nome
    const restOfName = parts.slice(1).join(' ');

    return `${abbreviatedFirst} ${restOfName}`;
  };

  // Calcola i nomi dei giorni della settimana per il mese selezionato
  const weekDays = useMemo(() => {
    return Array.from({ length: daysInMonth }, (_, i) => {
      const date = new Date(filterYear, filterMonth - 1, i + 1);
      return date.toLocaleDateString('en-GB', { weekday: 'short' }).charAt(0);
    });
  }, [filterYear, filterMonth, daysInMonth]);

  // Calcola indici dei weekend per il mese selezionato
  const weekendIndexes = useMemo(() => {
    return weekDays.map((wd, i) => {
      const date = new Date(filterYear, filterMonth - 1, i + 1);
      const day = date.getDay(); // 0=Sunday, 6=Saturday
      return (day === 0 || day === 6);
    });
  }, [weekDays, filterYear, filterMonth]);

  useEffect(() => {
    const yearMonth = `${filterYear}-${String(filterMonth).padStart(2, '0')}`;
    getAllAbsencesForMonth(yearMonth).then(entries => {
      const byUser = {};
      entries.forEach(v => {
        const day = parseInt(v.date.slice(8, 10), 10);
        if (!byUser[String(v.user_id)]) byUser[String(v.user_id)] = new Map();
        byUser[String(v.user_id)].set(day, v);
      });
      setAbsencesByUserId(byUser);
    }).catch(() => setAbsencesByUserId({}));
  }, [filterYear, filterMonth]);

  const userIdByUsername = useMemo(() => {
    const map = {};
    (managedUsers || []).forEach(u => {
      if (!u.id) return;
      const uid = String(u.id);
      if (u.name) map[u.name] = uid;
      if (u.username) map[u.username] = uid;
      if (u.first_name && u.last_name) map[`${u.first_name} ${u.last_name}`] = uid;
    });
    Object.entries(pivotData || {}).forEach(([uname, days]) => {
      if (map[uname]) return;
      const firstDay = Object.values(days)[0];
      if (firstDay?.user_id) map[uname] = String(firstDay.user_id);
    });
    return map;
  }, [managedUsers, pivotData]);

  const isUserAbsent = (username, dayNumber) => {
    const uid = userIdByUsername[username];
    if (!uid) return null;
    return absencesByUserId[uid]?.get(dayNumber) || null;
  };

  // Tabella principale: sempre per utente gestito (All)
  useEffect(() => {
    // Evita il fetch finché gli utenti gestiti non sono stati inizializzati
    if (!managedUsersInitialized) {
      console.log('Managed users not initialized yet, skipping pivot data fetch');
      return;
    }
    if (!managedUsersLoaded || managedUsers.length === 0) {
      console.log('Managed users not loaded yet, skipping pivot data fetch');
      return;
    }

    // Crea una chiave univoca per questa combinazione di parametri
    const yearMonth = `${filterYear}-${String(filterMonth).padStart(2, '0')}`;
    const selectedUserIds = filterUsers.length === 0
      ? managedUsers.map(u => u.id).sort().join(',')
      : managedUsers
        .filter(u => filterUsers.includes(u.name || u.username))
        .map(u => u.id)
        .sort()
        .join(',');
    const activityIdsKey = (filterActivityIds && filterActivityIds.length > 0)
      ? filterActivityIds.sort().join(',')
      : 'all';

    const fetchKey = `${yearMonth}-${selectedUserIds}-${activityIdsKey}`;

    // Evita chiamate duplicate per la stessa combinazione di parametri
    if (isFetchingPivotData.current) {
      return;
    }

    // Se la chiave è la stessa, evita la chiamata (dati già caricati)
    if (lastPivotFetchKey.current === fetchKey) {
      return;
    }

    // Reset del flag se la chiave è diversa (nuovi parametri)
    if (lastPivotFetchKey.current && lastPivotFetchKey.current !== fetchKey) {
      isFetchingPivotData.current = false;
    }

    isFetchingPivotData.current = true;
    lastPivotFetchKey.current = fetchKey;
    setPivotLoading(true);
    setPivotError(null);
    // Svuota i dati pivot per evitare flash di dati precedenti
    setPivotData({});

    const fetchPivotData = async () => {
      try {
        // Determina quali utenti includere
        const userIdsArray = filterUsers.length === 0
          ? managedUsers.map(u => u.id)
          : managedUsers
            .filter(u => filterUsers.includes(u.name || u.username))
            .map(u => u.id);

        console.log('Fetching worklogs for managed users:', userIdsArray);

        // Per ogni utente, recupera i worklog del mese
        const allWorklogs = [];
        const activityIdsParam = (filterActivityIds && filterActivityIds.length > 0)
          ? `&activity_ids=${encodeURIComponent(filterActivityIds.join(','))}`
          : '';

        // Fetch worklogs per tutti gli utenti selezionati
        const worklogPromises = userIdsArray.map(async (userId) => {
          try {
            const response = await fetch(`${API_BASE_URL}/monthly_worklog_summary_user_project?user_id=${encodeURIComponent(userId)}&year_month=${yearMonth}${activityIdsParam}`);
            if (!response.ok) {
              console.error(`Error fetching worklogs for user ${userId}:`, response.status);
              return [];
            }
            const data = await response.json();
            return (data.data || []).map(item => ({
              ...item,
              user_id: userId,
              username: item.username || managedUsers.find(u => u.id === userId)?.username || 'Unknown'
            }));
          } catch (err) {
            console.error(`Error fetching worklogs for user ${userId}:`, err);
            return [];
          }
        });

        const worklogArrays = await Promise.all(worklogPromises);
        allWorklogs.push(...worklogArrays.flat());

        // Raggruppa per utente
        const byUser = {};
        allWorklogs.forEach(item => {
          const userName = item.username || managedUsers.find(u => u.id === item.user_id)?.name || 'Unknown User';
          if (!byUser[userName]) byUser[userName] = {};
          const day = new Date(item.work_date).getDate();
          // Se ci sono più worklog per lo stesso utente/giorno, somma gli effort
          if (byUser[userName][day]) {
            byUser[userName][day].total_effort = (Number(byUser[userName][day].total_effort) || 0) + (Number(item.total_effort) || 0);
          } else {
            byUser[userName][day] = item;
          }
        });

        // Assicurati che anche gli utenti senza worklog compaiano nel pivot
        const selectedUserNames = (filterUsers.length === 0)
          ? managedUsers.map(u => u.name || u.username)
          : managedUsers.filter(u => filterUsers.includes(u.name || u.username)).map(u => u.name || u.username);
        selectedUserNames.forEach(name => {
          if (!byUser[name]) {
            byUser[name] = {}; // riga vuota per utenti senza worklog
          }
        });

        console.log('Setting pivot data from API response (by user):', Object.keys(byUser));
        setPivotData(byUser);
        isFetchingPivotData.current = false;
      } catch (err) {
        console.error('Error in fetchPivotData:', err);
        setPivotError(`Error loading data: ${err.message}`);
        isFetchingPivotData.current = false;
      } finally {
        setPivotLoading(false);
      }
    };

    fetchPivotData();
  }, [managedUsersInitialized, filterMonth, filterYear, managedUsers, filterUsers, filterActivityIds, managedUsersLoaded]);

  // Reset della selezione utente quando cambiano i filtri
  useEffect(() => {
    setSelectedUserForDetails(null);
  }, [filterUsers, filterMonth, filterYear]);

  // Tabella dettagliata: fetch pivot per utente selezionato (mostra progetti per quell'utente quando si clicca sul nome)
  useEffect(() => {
    if (!selectedUserForDetails) {
      setTeamDetailsLoading(false);
      return;
    }

    // Imposta loading state
    setTeamDetailsLoading(true);

    const yearMonth = `${filterYear}-${String(filterMonth).padStart(2, '0')}`;
    const selectedUserId = managedUsers.find(u => (u.name || u.username) === selectedUserForDetails)?.id;

    if (!selectedUserId) {
      setTeamDetailsLoading(false);
      return;
    }

    // Fetch worklog data per l'utente selezionato (passa activity_ids se selezionate)
    const activityIdsParam = (filterActivityIds && filterActivityIds.length > 0)
      ? `&activity_ids=${encodeURIComponent(filterActivityIds.join(','))}`
      : '';
    fetch(`${API_BASE_URL}/monthly_worklog_summary_user_project?user_id=${encodeURIComponent(selectedUserId)}&year_month=${yearMonth}${activityIdsParam}`)
      .then(res => res.json())
      .then(data => {
        // data: [{ username, project_name, work_date, total_effort, ... }]
        const byProject = {};
        if (Array.isArray(data.data)) {
          data.data.forEach(item => {
            const projectName = item.project_name || 'Unknown Project';
            if (!byProject[projectName]) byProject[projectName] = {};
            const day = new Date(item.work_date).getDate();
            // Se ci sono più worklog per lo stesso progetto/giorno, somma gli effort
            if (byProject[projectName][day]) {
              byProject[projectName][day].total_effort = (Number(byProject[projectName][day].total_effort) || 0) + (Number(item.total_effort) || 0);
            } else {
              byProject[projectName][day] = item;
            }
          });
        }
        setDetailsPivotData(byProject);
        setTeamDetailsLoading(false);
      })
      .catch(err => {
        console.error('Error loading details:', err);
        setTeamDetailsLoading(false);
      });
  }, [selectedUserForDetails, filterMonth, filterYear, managedUsers, filterActivityIds]);

  // Per People Manager View, i dettagli mostrano progetti per l'utente selezionato
  // Non serve aggregazione per team, i dettagli sono già per progetto
  useEffect(() => {
    if (!selectedUserForDetails) {
      setTeamPivotData({});
      return;
    }
    // I dettagli sono già per progetto, quindi usiamo direttamente detailsPivotData
    setTeamPivotData(detailsPivotData || {});
  }, [detailsPivotData, selectedUserForDetails]);

  // Funzione per renderizzare la tabella pivot dettagliata per progetti
  function renderProjectPivotTable({ projectPivotData, filterYear, filterMonth }) {
    const allProjects = projectPivotData ? Object.keys(projectPivotData) : [];
    if (allProjects.length === 0) return null;

    const daysInMonthLocal = new Date(filterYear, filterMonth, 0).getDate();
    const weekDays = Array.from({ length: daysInMonthLocal }, (_, i) => {
      const date = new Date(filterYear, filterMonth - 1, i + 1);
      return date.toLocaleDateString('en-GB', { weekday: 'short' }).charAt(0);
    });
    const weekendIndexes = weekDays.map((wd, i) => {
      const date = new Date(filterYear, filterMonth - 1, i + 1);
      const day = date.getDay();
      return (day === 0 || day === 6);
    });

    const projectTotals = allProjects.map(p => {
      if (projectPivotData && projectPivotData[p]) {
        return Math.round((Object.values(projectPivotData[p]).reduce((a, b) => a + (Number(b?.total_effort) || 0), 0) / 60) * 100) / 100;
      }
      return 0;
    });

    const dayTotals = Array.from({ length: daysInMonthLocal }, (_, i) => {
      let total = 0;
      allProjects.forEach(p => {
        total += Number(projectPivotData?.[p]?.[i + 1]?.total_effort) || 0;
      });
      return Math.round((total / 60) * 100) / 100;
    });
    const grandTotal = Math.round(projectTotals.reduce((a, b) => a + b, 0) * 100) / 100;

    return (
      <TableContainer component={Paper} sx={{
        borderRadius: 2,
        bgcolor: 'white',
        p: 0,
        maxWidth: '100vw',
        overflowX: 'auto',
        boxShadow: 'none',
        border: '1px solid #f3f3f3',
        mt: 4
      }}>
        <Table size="small" sx={{
          minWidth: tableDimensions.minWidth,
          tableLayout: 'fixed',
          fontSize: '0.85rem',
          width: '100%',
          bgcolor: 'white'
        }}>
          <colgroup>
            <col style={{ width: tableDimensions.firstColWidth }} />
            {Array.from({ length: daysInMonthLocal }).map((_, i) => (
              <col key={i} style={{ width: tableDimensions.dayColWidth }} />
            ))}
            <col style={{ width: tableDimensions.totalColWidth }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2} sx={{
                bgcolor: '#5ab031',
                color: 'white',
                fontWeight: 'normal',
                fontSize: '0.85rem',
                border: 'none',
                borderRight: '0px solid #e0e0e0',
                textAlign: 'center',
                minWidth: 120,
                maxWidth: 160,
                width: 140,
                position: 'sticky',
                left: 0,
                zIndex: 3,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                '@media (max-width: 900px)': {
                  position: 'sticky',
                  left: 0,
                  zIndex: 3,
                  boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                }
              }}>
                Project
              </TableCell>
              {weekDays.map((wd, i) => (
                <TableCell key={i} sx={{ bgcolor: '#5ab031', color: 'white', border: '0px solid #f3f3f3', textAlign: 'center', fontWeight: 'normal', fontSize: '0.85rem', p: 0.5 }}>{wd}</TableCell>
              ))}
              <TableCell rowSpan={2} sx={{
                bgcolor: '#5ab031',
                color: 'white',
                fontWeight: 'normal',
                fontSize: '0.85rem',
                p: 0.5,
                border: '0px solid #f3f3f3',
                position: 'sticky',
                right: 0,
                zIndex: 3,
                textAlign: 'center',
                minWidth: 60,
                maxWidth: 80,
                width: 60,
                '@media (max-width: 900px)': {
                  position: 'sticky',
                  right: 0,
                  zIndex: 3,
                  boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                }
              }}>Tot</TableCell>
            </TableRow>
            <TableRow>
              {Array.from({ length: daysInMonthLocal }, (_, i) => (
                <TableCell key={i + 1} sx={{ bgcolor: '#5ab031', color: 'white', border: '0px solid #f3f3f3', textAlign: 'center', fontWeight: 'normal', fontSize: '0.85rem', p: 0.5 }}>{i + 1}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {allProjects.map((p, idx) => (
              <TableRow
                key={p}
                hover
                sx={{ '&:hover': { bgcolor: '#e6f4ea' } }}
              >
                <TableCell
                  className="pivot-user-col"
                  sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    minWidth: 120,
                    maxWidth: 160,
                    width: 140,
                    p: 0.5,
                    border: '1px solid #f3f3f3',
                    borderRight: '2px solid #5ab031',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textAlign: 'left',
                    bgcolor: 'white'
                  }}
                >
                  {p}
                </TableCell>
                {Array.from({ length: daysInMonthLocal }, (_, i) => {
                  const value = projectPivotData?.[p]?.[i + 1]?.total_effort;
                  const displayValue = value ? Math.round((Number(value) / 60) * 100) / 100 : '';
                  return (
                    <TableCell key={i + 1} sx={{
                      textAlign: 'center',
                      bgcolor: weekendIndexes[i] ? 'rgba(55,55,55,0.08)' : 'white',
                      fontSize: '0.7rem',
                      p: 0.5,
                      border: '1px solid #f3f3f3',
                      fontWeight: 'normal',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontVariantNumeric: 'tabular-nums',
                      '&:hover': { bgcolor: '#aace7c78' }
                    }}>{displayValue}</TableCell>
                  );
                })}
                <TableCell sx={{
                  textAlign: 'center',
                  fontSize: '0.7rem',
                  p: 0.5,
                  border: '1px solid #f3f3f3',
                  position: 'sticky',
                  right: 0,
                  zIndex: 2,
                  borderLeft: '2px solid #5ab031',
                  minWidth: 60,
                  maxWidth: 80,
                  width: 60,
                  bgcolor: 'white !important',
                  color: 'black !important',
                  fontWeight: 'bold',
                  '@media (max-width: 900px)': {
                    position: 'sticky',
                    right: 0,
                    zIndex: 2,
                    boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                  }
                }}>{projectTotals[idx]}</TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ bgcolor: '#248b7e' }}>
              <TableCell
                className="pivot-user-col"
                sx={{
                  bgcolor: 'white',
                  position: 'sticky',
                  left: 0,
                  zIndex: 2,
                  minWidth: 120,
                  maxWidth: 160,
                  width: 140,
                  p: 0.5,
                  border: '1px solid #f3f3f3',
                  borderRight: '2px solid #5ab031',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  textAlign: 'right',
                  borderTop: '2px solid #5ab031',
                  '@media (max-width: 900px)': {
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                  }
                }}>Tot</TableCell>
              {dayTotals.map((tot, i) => (
                <TableCell key={i + 1} sx={{ bgcolor: 'white !important', color: 'black !important', textAlign: 'center', fontSize: '0.7rem', p: 0.5, border: '1px solid #f3f3f3', borderTop: '2px solid #5ab031', fontWeight: 'bold' }}>{tot}</TableCell>
              ))}
              <TableCell sx={{
                bgcolor: 'white !important',
                color: 'black !important',
                textAlign: 'center',
                fontSize: '0.7rem',
                p: 0.5,
                border: '1px solid #f3f3f3',
                position: 'sticky',
                right: 0,
                zIndex: 2,
                borderLeft: '2px solid #5ab031',
                borderTop: '2px solid #5ab031',
                minWidth: 60,
                maxWidth: 80,
                width: 60,
                fontWeight: 'bold',
                '@media (max-width: 900px)': {
                  position: 'sticky',
                  right: 0,
                  zIndex: 2,
                  boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                }
              }}>{grandTotal}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  // Ordina gli utenti alfabeticamente per una visualizzazione consistente
  const filteredPivotData = useMemo(() => {
    console.log('Using pivot data from API:', Object.keys(pivotData));
    // Ordina gli utenti alfabeticamente
    const sortedEntries = Object.entries(pivotData).sort(([a], [b]) => 
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
    return Object.fromEntries(sortedEntries);
  }, [pivotData]);

  // Calcola totali per riga e per giorno (basati sui dati filtrati, convertiti da minuti a ore)
  const rowTotals = useMemo(() => {
    const totals = {};
    Object.entries(filteredPivotData).forEach(([rowKey, days]) => {
      totals[rowKey] = Math.round((Object.values(days).reduce((sum, obj) => sum + (Number(obj?.total_effort) || 0), 0) / 60) * 100) / 100;
    });
    return totals;
  }, [filteredPivotData]);
  const dayTotals = useMemo(() => {
    const totals = {};
    for (let d = 1; d <= daysInMonth; d++) {
      totals[d] = Math.round((Object.values(filteredPivotData).reduce((sum, days) => sum + (Number(days[d]?.total_effort) || 0), 0) / 60) * 100) / 100;
    }
    return totals;
  }, [filteredPivotData, daysInMonth]);
  const grandTotal = useMemo(() => Math.round(Object.values(rowTotals).reduce((sum, v) => sum + v, 0) * 100) / 100, [rowTotals]);

  return (
    <Box>
      <Box my={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" gutterBottom sx={{ fontSize: '1.5rem' }}>Monthly People Manager View</Typography>
        </Box>
        {/* Filtri progetto e intervallo date */}
        <Box
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent={{ xs: 'flex-start', md: 'flex-start' }}
          mb={2}
        >
          <Autocomplete
            multiple
            disabled={!managedUsersLoaded}
            disableCloseOnSelect={true}
            options={[
              { id: 'all', name: 'All', isSpecial: true },
              ...managedUsers
            ]}
            getOptionLabel={(option) => option.name || option.username || option.email}
            value={managedUsers.filter(u => filterUsers.includes(u.name || u.username))}
            onChange={(event, newValue) => {
              // Handle special options
              const hasAll = newValue.some(v => v.id === 'all');

              if (hasAll) {
                setFilterUsers([]);
              } else {
                setFilterUsers(newValue.map(u => u.name || u.username));
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Managed Users"
                placeholder="Search users..."
                size="small"
                sx={{ minWidth: 300 }}
              />
            )}
            renderOption={(props, option, { selected }) => {
              if (option.isSpecial) {
                return (
                  <Box component="li" {...props}>
                    <Checkbox
                      checked={option.id === 'all' ? filterUsers.length === 0 : false}
                      sx={{ mr: 1, pointerEvents: 'none' }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontStyle: 'italic', color: 'black' }}
                    >
                      {option.name}
                    </Typography>
                  </Box>
                );
              }

              return (
                <Box component="li" {...props}>
                  <Checkbox
                    checked={selected}
                    sx={{ mr: 1 }}
                  />
                  <Box>
                    <Typography variant="body2">
                      {option.name || option.username}
                    </Typography>
                    {option.email && (
                      <Typography variant="caption" color="textSecondary">
                        {option.email}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }}
            renderTags={(value, getTagProps) =>
              value.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  All users
                </Typography>
              ) : value.length === 1 && value[0].id === 'all' ? (
                <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'black' }}>
                  All
                </Typography>
              ) : (
                <Typography variant="body2">
                  {value.length} selected
                </Typography>
              )
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="No users found"
            clearOnBlur={false}
            selectOnFocus
            handleHomeEndKeys
            filterOptions={(options, { inputValue }) => {
              // Always show special options at the top
              const specialOptions = options.filter(opt => opt.isSpecial);
              const regularOptions = options.filter(opt => !opt.isSpecial);

              // Filter regular options based on input
              const filteredRegular = regularOptions.filter(option =>
                (option.name || option.username || '').toLowerCase().includes(inputValue.toLowerCase()) ||
                (option.email && option.email.toLowerCase().includes(inputValue.toLowerCase()))
              );

              return [...specialOptions, ...filteredRegular];
            }}
          />
          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel id="filter-month-label">Month</InputLabel>
            <Select
              labelId="filter-month-label"
              value={filterMonth}
              label="Month"
              onChange={e => setFilterMonth(Number(e.target.value))}
            >
              {monthNames.map((name, idx) => (
                <MenuItem key={idx + 1} value={idx + 1}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Filtro Activity */}
          <Autocomplete
            multiple
            disableCloseOnSelect={true}
            options={[
              { id: 'all', name: 'All', isSpecial: true },
              ...activities.map(ac => ({ id: ac.id, name: ac.name }))
            ]}
            getOptionLabel={(option) => option.name}
            value={activities
              .filter(ac => filterActivityIds.includes(ac.id))
              .map(ac => ({ id: ac.id, name: ac.name }))}
            onChange={(event, newValue) => {
              const hasAll = newValue.some(v => v.id === 'all');
              if (hasAll) {
                setFilterActivityIds([]);
              } else {
                setFilterActivityIds(newValue.map(v => v.id));
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Activities"
                placeholder="Search activities..."
                size="small"
                sx={{ minWidth: 260 }}
              />
            )}
            renderOption={(props, option, { selected }) => {
              if (option.isSpecial) {
                return (
                  <Box component="li" {...props}>
                    <Checkbox
                      checked={option.id === 'all' ? filterActivityIds.length === 0 : false}
                      sx={{ mr: 1, pointerEvents: 'none' }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontStyle: 'italic', color: 'black' }}
                    >
                      {option.name}
                    </Typography>
                  </Box>
                );
              }
              return (
                <Box component="li" {...props}>
                  <Checkbox checked={selected} sx={{ mr: 1 }} />
                  <Typography variant="body2">{option.name}</Typography>
                </Box>
              );
            }}
            renderTags={(value) =>
              value.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  All activities
                </Typography>
              ) : value.length === 1 && value[0].id === 'all' ? (
                <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'black' }}>
                  All
                </Typography>
              ) : (
                <Typography variant="body2">{value.length} selected</Typography>
              )
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="No activities found"
            clearOnBlur={false}
            selectOnFocus
            handleHomeEndKeys
            filterOptions={(options, { inputValue }) => {
              const specialOptions = options.filter(opt => opt.isSpecial);
              const regularOptions = options.filter(opt => !opt.isSpecial);
              const filteredRegular = regularOptions.filter(option =>
                option.name.toLowerCase().includes(inputValue.toLowerCase())
              );
              return [...specialOptions, ...filteredRegular];
            }}
          />
          <FormControl sx={{ minWidth: 100 }} size="small">
            <InputLabel id="filter-year-label">Year</InputLabel>
            <Select
              labelId="filter-year-label"
              value={filterYear}
              label="Year"
              onChange={e => setFilterYear(Number(e.target.value))}
            >
              {yearOptions.map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" size="small" onClick={() => { setFilterUsers([]); setFilterMonth(now.getMonth() + 1); setFilterYear(now.getFullYear()); }}>Reset</Button>
        </Box>
        {!managedUsersLoaded ? (
          <CircularProgress />
        ) : pivotLoading ? (
          <CircularProgress />
        ) : pivotError ? (
          <Alert severity="error">{pivotError}</Alert>
        ) : (Object.keys(filteredPivotData).length === 0) ? (
          <Alert severity="info">{filterUsers.length > 0 ? `No worklogs found for the selected user(s) in this period.` : `For this date, no worklogs are present.`}</Alert>
        ) : (
          <>
            <TableContainer component={Paper} sx={{
              borderRadius: 2,
              bgcolor: 'white',
              p: 0,
              maxWidth: '100vw',
              overflowX: 'auto',
              boxShadow: 'none',
              border: '1px solid #f3f3f3'
            }}>
              <Table size="small" sx={{
                minWidth: tableDimensions.minWidth,
                tableLayout: 'fixed',
                fontSize: '0.85rem',
                width: '100%',
                bgcolor: 'white'
              }}>
                <colgroup>
                  <col style={{ width: tableDimensions.firstColWidth }} />
                  {Array.from({ length: daysInMonth }).map((_, i) => (
                    <col key={i} style={{ width: tableDimensions.dayColWidth }} />
                  ))}
                  <col style={{ width: tableDimensions.totalColWidth }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell
                      className="pivot-user-col"
                      rowSpan={2}
                      sx={{
                        bgcolor: '#5ab031',
                        border: 'none',
                        p: 0,
                        minWidth: 120,
                        maxWidth: 160,
                        width: 140,
                        color: 'white',
                        fontWeight: 'normal',
                        borderRight: '0px solid #e0e0e0',
                        position: 'sticky',
                        left: 0,
                        zIndex: 3,
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        '@media (max-width: 900px)': {
                          position: 'sticky',
                          left: 0,
                          zIndex: 3,
                          boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                      User
                    </TableCell>
                    {weekDays.map((wd, i) => {
                      return (
                        <TableCell key={i} sx={{ bgcolor: '#5ab031', color: 'white', border: '0px solid #f3f3f3', textAlign: 'center', fontWeight: 'normal', fontSize: '0.85rem', p: 0.5 }}>{wd}</TableCell>
                      );
                    })}
                    <TableCell sx={{
                      bgcolor: '#5ab031',
                      color: 'white',
                      fontWeight: 'normal',
                      fontSize: '0.85rem',
                      p: 0.5,
                      border: '0px solid #f3f3f3',
                      position: 'sticky',
                      right: 0,
                      zIndex: 3,
                      verticalAlign: 'middle',
                      textAlign: 'center',
                      minWidth: 60,
                      maxWidth: 80,
                      width: 60,
                      '@media (max-width: 900px)': {
                        position: 'sticky',
                        right: 0,
                        zIndex: 3,
                        boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                      }
                    }} rowSpan={2}>Tot</TableCell>
                  </TableRow>
                  <TableRow>
                    {Array.from({ length: daysInMonth }, (_, i) => {
                      return (
                        <TableCell key={i + 1} sx={{ bgcolor: '#5ab031', color: 'white', border: '0px solid #f3f3f3', textAlign: 'center', fontWeight: 'normal', fontSize: '0.85rem', p: 0.5 }}>{i + 1}</TableCell>
                      );
                    })}
                    {/* Nessuna TableCell qui per Tot, perché la cella sopra ha rowSpan=2 */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(filteredPivotData).map(([rowKey, days]) => (
                    <TableRow
                      key={rowKey}
                      hover
                      sx={{
                        '&:hover': { bgcolor: '#e6f4ea' }
                      }}
                    >
                      <TableCell
                        className="pivot-user-col"
                        sx={{
                          bgcolor: 'white',
                          color: '#5ab031',
                          position: 'sticky',
                          left: 0,
                          zIndex: 2,
                          minWidth: 120,
                          maxWidth: 160,
                          width: 140,
                          p: 0.5,
                          border: '1px solid #f3f3f3',
                          borderRight: '2px solid #5ab031',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          textAlign: 'left',
                          '@media (max-width: 900px)': {
                            position: 'sticky',
                            left: 0,
                            zIndex: 2,
                            boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <MuiTooltip title="View project details" arrow>
                            <span
                              style={{
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                fontWeight: 'normal',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.color = '#4a9b2a';
                                e.target.style.fontWeight = 'bold';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.color = '#5ab031';
                                e.target.style.fontWeight = 'normal';
                              }}
                              onClick={() => {
                                if (selectedUserForDetails === rowKey) {
                                  setSelectedUserForDetails(null);
                                } else {
                                  setSelectedUserForDetails(rowKey);
                                }
                              }}
                            >
                              {formatUserName(rowKey)}
                            </span>
                          </MuiTooltip>
                          {(() => {
                            const userObj = managedUsers.find(u => (u.name || u.username) === rowKey);
                            return userObj?.isPeopleManager ? (
                              <MuiTooltip title="View hierarchy" arrow>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    bgcolor: 'rgba(90, 176, 49, 0.1)',
                                    borderRadius: '4px',
                                    padding: '2px 4px',
                                    marginLeft: '4px',
                                    flexShrink: 0,
                                    '&:hover': {
                                      bgcolor: 'rgba(90, 176, 49, 0.2)',
                                      transform: 'scale(1.05)'
                                    },
                                    transition: 'all 0.2s'
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/monthly-people-mng-view/user/${userObj.id}`, {
                                      state: {
                                        filterYear,
                                        filterMonth,
                                        filterActivityIds,
                                        from: '/monthly-people-mng-view',
                                        selectedUser: {
                                          id: userObj.id,
                                          name: userObj.name || userObj.username,
                                          username: userObj.username,
                                          email: userObj.email
                                        }
                                      }
                                    });
                                  }}
                                >
                                  <SupervisorAccountIcon 
                                    sx={{ 
                                      fontSize: '1.1rem', 
                                      color: '#5ab031'
                                    }}
                                  />
                                </Box>
                              </MuiTooltip>
                            ) : null;
                          })()}
                        </Box>
                      </TableCell>
                      {Array.from({ length: daysInMonth }, (_, i) => {
                        const obj = days[i + 1];
                        const value = obj?.total_effort ? Math.round((Number(obj.total_effort) / 60) * 100) / 100 : '';
                        const isWeekend = weekendIndexes[i];
                        const dayNum = i + 1;
                        const absEntry = isUserAbsent(rowKey, dayNum);
                        return (
                          <MuiTooltip key={i + 1} title={absEntry ? `Absence (${absEntry.full_day ? 'full day' : 'partial'})${value ? ` · ${value}h` : ''}` : ''} arrow>
                          <TableCell sx={{ textAlign: 'center', bgcolor: absEntry ? (absEntry.full_day ? '#ffcdd2 !important' : '#fff9c4 !important') : isWeekend ? 'rgba(55,55,55,0.08)' : 'white', color: '#374151', fontSize: '0.7rem', p: 0.5, border: '1px solid #f3f3f3', fontWeight: 'normal', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontVariantNumeric: 'tabular-nums', '&:hover': { bgcolor: absEntry ? (absEntry.full_day ? '#ef9a9a !important' : '#fff176 !important') : '#aace7c78' } }}>
                            {obj ? (
                              <MuiTooltip
                                title={
                                  <span>
                                    {obj.ticket_id && <div><b>Ticket:</b> {obj.ticket_id}</div>}
                                    {obj.description && <div><b>Desc:</b> {obj.description}</div>}
                                    {obj.activity_name && <div><b>Activity:</b> {obj.activity_name}</div>}
                                    {obj.status_name && <div><b>Status:</b> {obj.status_name}</div>}
                                    {obj.effort_type_name && <div><b>Effort Type:</b> {obj.effort_type_name}</div>}
                                    {obj.total_effort && <div><b>Effort:</b> {Math.round((Number(obj.total_effort) / 60) * 100) / 100} h</div>}
                                  </span>
                                }
                                arrow
                              >
                                <span>{value}</span>
                              </MuiTooltip>
                            ) : absEntry ? <EventBusyIcon sx={{ fontSize: '0.7rem', color: absEntry.full_day ? '#c62828' : '#f57f17', verticalAlign: 'middle' }} /> : value}
                          </TableCell>
                          </MuiTooltip>
                        );
                      })}
                      <TableCell sx={{
                        bgcolor: 'white !important',
                        color: 'black !important',
                        textAlign: 'center',
                        fontSize: '0.7rem',
                        p: 0.5,
                        border: '1px solid #f3f3f3',
                        borderLeft: '2px solid #5ab031',
                        position: 'sticky',
                        right: 0,
                        zIndex: 2,
                        minWidth: 60,
                        maxWidth: 80,
                        width: 60,
                        fontWeight: 'bold',
                        '@media (max-width: 900px)': {
                          position: 'sticky',
                          right: 0,
                          zIndex: 2,
                          boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                        }
                      }}>{rowTotals[rowKey]}</TableCell>
                    </TableRow>
                  ))}
                  {/* Riga totale per giorno (ripristinata) */}
                  <TableRow sx={{ bgcolor: '#248b7e' }}>
                    <TableCell
                      sx={{
                        bgcolor: 'white',
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        minWidth: 120,
                        maxWidth: 160,
                        width: 140,
                        p: 0.5,
                        border: '1px solid #f3f3f3',
                        borderRight: '2px solid #5ab031',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        textAlign: 'right',
                        borderTop: '2px solid #5ab031',
                        '@media (max-width: 900px)': {
                          position: 'sticky',
                          left: 0,
                          zIndex: 2,
                          boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                        }
                      }}
                      className="pivot-user-col"
                    >
                      Tot
                    </TableCell>
                    {Array.from({ length: daysInMonth }, (_, i) => (
                      <TableCell key={i + 1} sx={{ bgcolor: 'white !important', color: 'black !important', textAlign: 'center', fontSize: '0.7rem', p: 0.5, border: '1px solid #f3f3f3', borderTop: '2px solid #5ab031', fontWeight: 'bold' }}>{dayTotals[i + 1]}</TableCell>
                    ))}
                    <TableCell sx={{
                      bgcolor: 'white !important',
                      color: 'black !important',
                      textAlign: 'center',
                      fontSize: '0.7rem',
                      p: 0.5,
                      border: '1px solid #f3f3f3',
                      borderLeft: '2px solid #5ab031',
                      borderTop: '2px solid #5ab031',
                      position: 'sticky',
                      right: 0,
                      zIndex: 2,
                      minWidth: 60,
                      maxWidth: 80,
                      width: 60,
                      fontWeight: 'bold',
                      '@media (max-width: 900px)': {
                        position: 'sticky',
                        right: 0,
                        zIndex: 2,
                        boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                      }
                    }}>{grandTotal}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Tabella dettagliata inline per utenti non-People Manager */}
        {selectedUserForDetails && (
          <Box sx={{ mt: 3 }}>
            {teamDetailsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ ml: 2, color: '#666' }}>
                  Loading project details...
                </Typography>
              </Box>
            ) : teamPivotData && Object.keys(teamPivotData).length > 0 ? (
              renderProjectPivotTable({
                projectPivotData: teamPivotData,
                filterYear,
                filterMonth
              })
            ) : (
              <Alert severity="info" sx={{ mt: 2 }}>
                No project data available for this user in the selected period.
              </Alert>
            )}
          </Box>
        )}

        {/* Note about effort in hours */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mt: 1,
          pr: 1
        }}>
          <Typography
            variant="caption"
            sx={{
              color: '#666',
              fontSize: '0.75rem',
              fontStyle: 'italic'
            }}
          >
            * effort in hours
          </Typography>
        </Box>
      </Box>

      {/* Informational note */}
      <Box sx={{
        mt: 3,
        p: 2,
        bgcolor: 'grey.50',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'grey.200'
      }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontStyle: 'italic',
            textAlign: 'center'
          }}
        >
          💡 <strong>Tip:</strong> Click on a user name to view detailed hours breakdown by project for that user for the selected month.
        </Typography>
      </Box>
    </Box>
  );
} 